import React, { useState, useEffect, useCallback } from 'react';
import { WebCryptoRamManager, RamKeyPair } from '../crypto/webcrypto-ram';
import { QRCodeSvg } from './QRCodeSvg';

export interface PairingGeneratorProps {
  cryptoManager: WebCryptoRamManager;
  coachId?: string;
  coachName?: string;
  clinicName?: string;
  onPairingGenerated?: (payload: any) => void;
}

export const PairingGenerator: React.FC<PairingGeneratorProps> = ({
  cryptoManager,
  coachId = 'coach_lead_01',
  coachName = 'Care Lead Practitioner',
  clinicName = 'Clinical Care Center',
  onPairingGenerated,
}) => {
  const [pin, setPin] = useState<string>('847291');
  const [keyPair, setKeyPair] = useState<RamKeyPair | null>(null);
  const [frozenPayloadString, setFrozenPayloadString] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [copiedPin, setCopiedPin] = useState<boolean>(false);
  const [copiedPayload, setCopiedPayload] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<'waiting' | 'connected'>('waiting');
  const [connectedClient, setConnectedClient] = useState<{ id: string; name: string } | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(900); // 15 minutes

  const generateNewPairingSession = useCallback(async () => {
    setIsGenerating(true);
    setConnectionStatus('waiting');
    setConnectedClient(null);
    setTimeLeft(900);
    try {
      const keys = await cryptoManager.generateKeyPair(2048);
      setKeyPair(keys);
      const randomPin = Math.floor(100000 + Math.random() * 900000).toString();
      setPin(randomPin);

      const fixedPayload = {
        scheme: 'luminousbreak://pair',
        coachId,
        coachName,
        clinicName,
        publicKeyPem: keys.publicKeyPem,
        keyFingerprint: keys.keyFingerprint,
        pinToken: randomPin,
        issuedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      };

      const payloadStr = JSON.stringify(fixedPayload, null, 2);
      setFrozenPayloadString(payloadStr);

      if (onPairingGenerated) {
        onPairingGenerated(fixedPayload);
      }
    } finally {
      setIsGenerating(false);
    }
  }, [cryptoManager, coachId, coachName, clinicName, onPairingGenerated]);

  useEffect(() => {
    generateNewPairingSession();
  }, [generateNewPairingSession]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const copyPin = () => {
    navigator.clipboard?.writeText(pin);
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  const copyPayload = () => {
    navigator.clipboard?.writeText(frozenPayloadString);
    setCopiedPayload(true);
    setTimeout(() => setCopiedPayload(false), 2000);
  };

  const simulateClientConnection = () => {
    setConnectionStatus('connected');
    setConnectedClient({
      id: 'usr_mock_9921',
      name: 'Alex M. (Client App Connected)',
    });
  };

  const formatMinutesSeconds = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 3-Step Instruction Guide Banner */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '20px 24px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', color: '#1B4332', fontWeight: 700 }}>
          🌿 How to Pair a Client in 3 Simple Steps
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{
              backgroundColor: '#E8F5E9',
              color: '#2D5A27',
              borderRadius: '8px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '13px',
              flexShrink: 0,
            }}>
              1
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#1E293B' }}>Open Client App</div>
              <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                Ask your client to open Luminous Break on their mobile device.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{
              backgroundColor: '#E8F5E9',
              color: '#2D5A27',
              borderRadius: '8px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '13px',
              flexShrink: 0,
            }}>
              2
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#1E293B' }}>Go to Therapist Pairing</div>
              <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                Have them tap <strong>Settings ➔ Therapist Pairing</strong>.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{
              backgroundColor: '#E8F5E9',
              color: '#2D5A27',
              borderRadius: '8px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '13px',
              flexShrink: 0,
            }}>
              3
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#1E293B' }}>Scan QR or Enter PIN</div>
              <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                Scan the QR code below with their phone camera or enter the 6-digit PIN.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Interactive Pairing Card */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        border: '1px solid #E2E8F0',
      }}>
        {/* Card Header & Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1E293B' }}>
              Active Client Pairing Session
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
              Practitioner: <strong>{coachName}</strong> • {clinicName}
            </p>
          </div>

          {/* Connection Status Pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {connectionStatus === 'connected' ? (
              <div style={{
                backgroundColor: '#D8F3DC',
                color: '#1B4332',
                border: '1px solid #95D5B2',
                padding: '8px 16px',
                borderRadius: '999px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span style={{ fontSize: '14px' }}>🟢</span>
                <span>{connectedClient?.name}</span>
              </div>
            ) : (
              <div style={{
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                border: '1px solid #FDE68A',
                padding: '8px 16px',
                borderRadius: '999px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span>⏳</span>
                <span>Waiting for client scan ({formatMinutesSeconds(timeLeft)} remaining)</span>
              </div>
            )}

            <button
              onClick={generateNewPairingSession}
              disabled={isGenerating}
              style={{
                backgroundColor: '#F1F5F9',
                border: '1px solid #CBD5E1',
                borderRadius: '8px',
                padding: '8px 14px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#334155',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              🔄 New Code
            </button>
          </div>
        </div>

        {/* Pairing Methods Grid (Side by Side) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center' }}>
          
          {/* Method A: Static High-Contrast QR Code */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#FAF8F5',
            borderRadius: '16px',
            padding: '28px',
            border: '1px solid #E6DFD5',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#4A7C59', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
              Option A: Point Camera at QR Code
            </div>

            <QRCodeSvg value={frozenPayloadString || `luminousbreak://pair?pin=${pin}`} size={210} />

            <div style={{ marginTop: '16px', fontSize: '12px', color: '#64748B' }}>
              Instant mobile camera scan • Automatic cryptographic key exchange
            </div>

            <button
              onClick={copyPayload}
              style={{
                marginTop: '12px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #CBD5E1',
                borderRadius: '8px',
                padding: '6px 14px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
              }}
            >
              {copiedPayload ? '✓ Copied Pairing Link!' : '📋 Copy Pairing Link'}
            </button>
          </div>

          {/* Method B: 6-Digit PIN Display */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#FAF8F5',
            borderRadius: '16px',
            padding: '28px',
            border: '1px solid #E6DFD5',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#4A7C59', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
              Option B: Enter 6-Digit PIN Code
            </div>

            {/* Individual Pin Digit Boxes */}
            <div style={{ display: 'flex', gap: '8px', margin: '8px 0 16px 0' }}>
              {(isGenerating ? '------' : pin).split('').map((digit, idx) => (
                <div
                  key={idx}
                  style={{
                    width: '44px',
                    height: '56px',
                    backgroundColor: '#FFFFFF',
                    border: '2px solid #2D5A27',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '26px',
                    fontWeight: 800,
                    color: '#1B4332',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  }}
                >
                  {digit}
                </div>
              ))}
            </div>

            <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '16px' }}>
              Ideal for remote teletherapy calls or manual entry
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={copyPin}
                style={{
                  backgroundColor: '#2D5A27',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(45,90,39,0.25)',
                }}
              >
                {copiedPin ? '✓ Copied PIN!' : '📋 Copy PIN'}
              </button>

              <button
                onClick={simulateClientConnection}
                style={{
                  backgroundColor: '#E0F2FE',
                  color: '#0369A1',
                  border: '1px solid #BAE6FD',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                ⚡ Simulate Pairing
              </button>
            </div>
          </div>
        </div>

        {/* Clean, Human-Friendly Security Status Footer (No Raw Hex Dumps) */}
        <div style={{
          marginTop: '28px',
          padding: '14px 20px',
          backgroundColor: '#F8FAFC',
          borderRadius: '12px',
          border: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '16px' }}>🔒</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#1E293B' }}>
                End-to-End Encrypted Handshake
              </span>
              <span style={{
                backgroundColor: '#E8F5E9',
                color: '#1B4332',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 600,
              }}>
                Hardware-Secured Session
              </span>
              <span style={{
                backgroundColor: '#F1F5F9',
                color: '#475569',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 500,
              }}>
                PIN #{pin}
              </span>
            </div>
          </div>
          <div style={{ fontSize: '11px', color: '#059669', fontWeight: 600 }}>
            ✓ Private Key isolated in browser RAM
          </div>
        </div>
      </div>
    </div>
  );
};

export default PairingGenerator;
